import React from 'react';
import { FileUploaderPreview } from '../../../../../components/FileUploaderPreview';
import { EvidenciaImg } from '../../../../../types/ot.types';
import { DIAS_SEMANA, HORAS } from '../../constants/centro.constants';
import {
    CatalogoItem,
    CentroFormData,
    HorarioDia,
    ProveedorOption,
    Slot,
    TipoCentroOption,
} from '../../types/centro.types';

interface TabGeneralProps {
    isEditMode: boolean;
    formData: CentroFormData;
    proveedores: ProveedorOption[];
    proveedorNombre: string;
    tiposCentro: TipoCentroOption[];
    bancos: CatalogoItem[];
    slots: Slot[];
    caratulaFiles: EvidenciaImg[];
    setCaratulaFiles: React.Dispatch<React.SetStateAction<EvidenciaImg[]>>;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onProveedorChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onHorarioChange: (id_dia: string, field: keyof HorarioDia, value: string | boolean) => void;
    onAddSlot: (e: React.MouseEvent) => void;
    onSlotChange: (index: number, field: keyof Slot, value: string | number) => void;
    onRemoveSlot: (index: number, e: React.MouseEvent) => void;
}

export const TabGeneral: React.FC<TabGeneralProps> = ({
    isEditMode,
    formData,
    proveedores,
    proveedorNombre,
    tiposCentro,
    bancos,
    slots,
    caratulaFiles,
    setCaratulaFiles,
    onInputChange,
    onProveedorChange,
    onHorarioChange,
    onAddSlot,
    onSlotChange,
    onRemoveSlot,
}) => (
    <>
        <h6 className="fw-bold border-bottom pb-2 mb-3">1. Datos del Taller</h6>
        <div className="row g-3 mb-4">
            <div id="centro-atencion-proveedor" className="col-md-6">
                <label className="fw-bold small mb-1">
                    Proveedor <span className="fw-normal text-muted">(Entidad Legal)</span>
                </label>
                {isEditMode ? (
                    <input type="text" className="form-control" value={proveedores.find(p => p.clave_persona_proveedor === formData.clave_persona_proveedor)?.nombre_completo || ''} disabled readOnly />
                ) : (
                    <select
                        className="form-select"
                        name="clave_persona_proveedor"
                        value={formData.clave_persona_proveedor}
                        onChange={onProveedorChange}
                        required
                        disabled={formData.clave_persona_proveedor !== ''}
                    >
                        <option value="">Seleccionar...</option>
                        {proveedores.map((p) => (
                            <option key={p.clave_persona_proveedor} value={p.clave_persona_proveedor}>
                                {p.nombre_completo}
                            </option>
                        ))}
                    </select>
                )}
            </div>
            <div id="centro-atencion-sucursal" className="col-md-6">
                <label className="fw-bold small mb-1">Nombre de Sucursal</label>
                <input
                    type="text"
                    className="form-control"
                    name="sucursal"
                    value={formData.sucursal}
                    onChange={onInputChange}
                    required
                />
            </div>
            <div id="centro-atencion-tipo" className="col-md-6">
                <label className="fw-bold small mb-1">Categoría de Centro</label>
                <select
                    className="form-select"
                    name="clave_tipo_centro"
                    value={formData.clave_tipo_centro}
                    onChange={onInputChange}
                    required
                >
                    <option value="">Elegir...</option>
                    {tiposCentro.map((t) => (
                        <option key={t.clave_tipo_centro} value={t.clave_tipo_centro}>
                            {t.nombre}
                        </option>
                    ))}
                </select>
            </div>
            <div className="col-md-6">
                <label className="fw-bold small mb-1">RFC de Proveedor</label>
                {isEditMode ? (
                    <input
                    type="text"
                    className="form-control"
                    name="rfc"
                    value={proveedores.find(p => p.clave_persona_proveedor === formData.clave_persona_proveedor)?.rfc || ''}
                    onChange={onInputChange}
                    maxLength={13}
                    disabled
                    readOnly
                />
                ) 
                : (<input
                    type="text"
                    className="form-control"
                    name="rfc"
                    value={formData.rfc}
                    onChange={onInputChange}
                    maxLength={13}
                    disabled
                    readOnly
                />)
                }
                
            </div>
        </div>

        <h6 className="fw-bold border-bottom pb-2 mb-3 mt-5">2. Datos Bancarios y Fiscales</h6>
        <div className="alert alert-light border small text-muted mb-4">
            <i className="fas fa-info-circle me-1 text-primary" /> Al crear un centro nuevo, se heredan los datos
            bancarios del proveedor, pero puede modificarlos si esta sucursal opera con cuentas independientes.
        </div>
        <div className="row g-4 mb-4">
            <div id="centro-atencion-banco" className="col-md-6">
                <label className="fw-bold mb-1 small text-muted">Banco Predeterminado</label>
                <select
                    className="form-select shadow-sm"
                    name="clave_banco"
                    value={formData.clave_banco || ''}
                    onChange={onInputChange}
                >
                    <option value="">Elegir...</option>
                    {bancos.map((b) => (
                        <option key={b.id ?? b.clave_banco} value={b.id ?? b.clave_banco}>
                            {b.nombre ?? b.banco}
                        </option>
                    ))}
                </select>
            </div>
            <div id="centro-atencion-clabe" className="col-md-6">
                <label className="fw-bold small mb-1">CLABE Interbancaria</label>
                <input
                    type="text"
                    className="form-control"
                    name="clabe"
                    value={formData.clabe}
                    onChange={onInputChange}
                    maxLength={18}
                    pattern="[0-9]{18}"
                    title="Debe contener exactamente 18 dígitos numéricos"
                    placeholder="18 dígitos"
                />
            </div>
            <div id="centro-atencion-caratula" className="col-md-6">
                <FileUploaderPreview
                    label="Carátula de Estado de Cuenta"
                    archivos={caratulaFiles}
                    setArchivos={setCaratulaFiles}
                    accept="application/pdf"
                    multiple={false}
                />
            </div>
        </div>

        <h6 className="fw-bold border-bottom pb-2 mb-3 mt-5">3. Directorio de Contactos</h6>
        <div className="row g-4 mb-4">
            {[
                {
                    title: '1. Programación',
                    fields: [
                        { name: 'asesor', label: 'Nombre (Asesor)', required: true },
                        { name: 'telefono', label: 'Teléfono', required: true },
                        { name: 'email', label: 'Email', type: 'email', required: true },
                    ],
                },
                {
                    title: '2. Administración',
                    fields: [
                        { name: 'contacto_administracion_nombre', label: 'Nombre' },
                        { name: 'contacto_administracion_telefono', label: 'Teléfono' },
                        { name: 'contacto_administracion_correo', label: 'Email', type: 'email' },
                    ],
                },
                {
                    title: '3. Facturación',
                    fields: [
                        { name: 'contacto_facturacion_nombre', label: 'Nombre' },
                        { name: 'contacto_facturacion_telefono', label: 'Teléfono' },
                        { name: 'contacto_facturacion_correo', label: 'Email', type: 'email' },
                    ],
                },
            ].map((section) => (
                <div
                    className="col-lg-4"
                    key={section.title}
                    id={
                        section.title.includes('Programación')
                            ? 'centro-atencion-contacto-programacion'
                            : section.title.includes('Administración')
                              ? 'centro-atencion-contacto-admin'
                              : 'centro-atencion-contacto-facturacion'
                    }
                >
                    <div className="card shadow-sm border-0 border-top border-3">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3">{section.title}</h6>
                            {section.fields.map((field) => (
                                <div className="mb-2" key={field.name}>
                                    <label className="small mb-1">{field.label}</label>
                                    <input
                                        type={field.type ?? 'text'}
                                        className="form-control form-control-sm"
                                        name={field.name}
                                        value={formData[field.name as keyof CentroFormData] as string}
                                        onChange={onInputChange}
                                        required={'required' in field ? field.required : false}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>

        <h6 className="fw-bold border-bottom pb-2 mb-3 mt-5">4. Horarios y Slots</h6>
        <div className="row g-4">
            <div id="centro-atencion-horarios" className="col-lg-6">
                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        {DIAS_SEMANA.map((dia) => {
                            const h = formData.horarios.find((x) => x.id_dia === dia.id);
                            if (!h) return null;
                            return (
                                <div key={dia.id} className="row g-2 align-items-center mb-2 pb-2 border-bottom">
                                    <div className="col-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`horario-activo-${dia.id}`}
                                                checked={h.activo}
                                                onChange={(e) => onHorarioChange(dia.id, 'activo', e.target.checked)}
                                            />
                                            <label className="form-check-label small" htmlFor={`horario-activo-${dia.id}`}>
                                                {dia.nombre}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <select
                                            className="form-select form-select-sm"
                                            value={h.hora_inicio}
                                            onChange={(e) => onHorarioChange(dia.id, 'hora_inicio', e.target.value)}
                                            disabled={!h.activo}
                                            aria-label={`Hora inicio ${dia.nombre}`}
                                        >
                                            {HORAS.map((x) => (
                                                <option key={x} value={x}>
                                                    {x}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-4">
                                        <select
                                            className="form-select form-select-sm"
                                            value={h.hora_fin}
                                            onChange={(e) => onHorarioChange(dia.id, 'hora_fin', e.target.value)}
                                            disabled={!h.activo}
                                            aria-label={`Hora fin ${dia.nombre}`}
                                        >
                                            {HORAS.map((x) => (
                                                <option key={x} value={x}>
                                                    {x}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div id="centro-atencion-slots" className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                        <div className="form-check form-switch mb-3 pb-3 border-bottom">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                name="asignacion_automatica"
                                checked={formData.asignacion_automatica}
                                onChange={onInputChange}
                                id="asig_auto"
                            />
                            <label className="form-check-label fw-bold" htmlFor="asig_auto">
                                Asignación Automática
                            </label>
                        </div>
                        {formData.asignacion_automatica && (
                            <>
                                <div className="text-end mb-2">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={onAddSlot}>
                                        <i className="fas fa-plus" /> Bloque
                                    </button>
                                </div>
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Horario</th>
                                            <th>Cupo</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {slots.map((s, i) => (
                                            <tr key={`slot-${i}-${s.id}`}>
                                                <td>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={s.horario}
                                                        onChange={(e) => onSlotChange(i, 'horario', e.target.value)}
                                                    >
                                                        {HORAS.map((x) => (
                                                            <option key={x} value={x}>
                                                                {x}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={s.cantidad}
                                                        onChange={(e) => onSlotChange(i, 'cantidad', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm text-danger"
                                                        onClick={(e) => onRemoveSlot(i, e)}
                                                        aria-label="Eliminar slot"
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </>
);
